import { axiosInstance } from "./networking";

/**
 * Client-side error reporting. Everything that reaches the server goes through
 * `reportClientError`, which POSTs to `/feedback/client-error` (the
 * axiosInstance baseURL is REACT_APP_API_DOMAIN) and never throws.
 *
 * Sources:
 *  - "boundary"            React error boundaries (components/common/ErrorBoundary)
 *  - "window.error"        uncaught exceptions outside React's render
 *  - "unhandledrejection"  promises nobody caught (often an axios call)
 */
export type ClientErrorSource = "boundary" | "window.error" | "unhandledrejection";

export interface ClientErrorReport {
  message: string;
  stack?: string;
  componentStack?: string;
  source: ClientErrorSource;
  route?: string;
}

/** One report per identical message inside this window. */
export const MESSAGE_WINDOW_MS = 60_000;
/** Hard cap per page load, so a render loop cannot flood the server. */
export const MAX_REPORTS_PER_PAGE = 20;

const MAX_MESSAGE_CHARS = 1_000;
const MAX_STACK_CHARS = 8_000;

let reportsSent = 0;
const lastReportedAt = new Map<string, number>();
const lastToastedAt = new Map<string, number>();
const subscribers = new Set<(msg: string) => void>();
let handlersInstalled = false;

const clip = (value: string | undefined, max: number): string | undefined =>
  value && value.length > max ? value.slice(0, max) : value;

/** Browser noise that says nothing about our code. */
export const isNoiseMessage = (message: string | undefined | null): boolean => {
  const text = (message || "").trim();
  return (
    !text ||
    text === "Script error." ||
    text === "Script error" ||
    /ResizeObserver loop/i.test(text)
  );
};

const currentRoute = (): string => {
  try {
    return window.location.pathname + window.location.search;
  } catch {
    return "";
  }
};

const currentUser = (): { userId?: string; username?: string } => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}") || {};
    return {
      userId: user._id ? String(user._id) : undefined,
      username: user.username ? String(user.username) : undefined,
    };
  } catch {
    return {};
  }
};

/** True when `key` was seen inside the window; records it otherwise. */
const throttled = (seen: Map<string, number>, key: string): boolean => {
  const now = Date.now();
  const last = seen.get(key);
  if (last !== undefined && now - last < MESSAGE_WINDOW_MS) return true;
  seen.set(key, now);
  return false;
};

/**
 * Send one error to the server. Fire-and-forget: rate limited, noise filtered,
 * and every failure (400, 429, network) is swallowed.
 */
export const reportClientError = (report: ClientErrorReport): void => {
  try {
    const message = clip(String(report.message ?? ""), MAX_MESSAGE_CHARS) || "";
    if (isNoiseMessage(message)) return;
    if (reportsSent >= MAX_REPORTS_PER_PAGE) return;
    // Keyed on source + message: in development React re-dispatches a
    // boundary-caught error as a window "error" event, and the boundary's own
    // report (the one carrying the component stack) must not be dropped by it.
    if (throttled(lastReportedAt, `${report.source}:${message}`)) return;
    reportsSent += 1;

    const { userId, username } = currentUser();
    const body = {
      message,
      stack: clip(report.stack, MAX_STACK_CHARS),
      componentStack: clip(report.componentStack, MAX_STACK_CHARS),
      route: report.route ?? currentRoute(),
      source: report.source,
      userId,
      username,
      buildSha: process.env.REACT_APP_BUILD_SHA,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      timestamp: new Date().toISOString(),
      env: process.env.NODE_ENV,
    };

    Promise.resolve(
      axiosInstance.post("/feedback/client-error", body, { timeout: 10_000 }),
    ).catch((err: any) => {
      // eslint-disable-next-line no-console
      console.debug("client error report not delivered", err?.response?.status ?? err?.message);
    });
  } catch {
    // Reporting must never be the thing that breaks the page.
  }
};

/** Subscribe to user-visible global errors (for a toast). Returns unsubscribe. */
export const subscribeToGlobalErrors = (cb: (msg: string) => void): (() => void) => {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
};

const notifySubscribers = (message: string) => {
  // Same per-message window as the server reports, so a failing poll does not
  // re-open the toast every few seconds.
  if (throttled(lastToastedAt, message)) return;
  subscribers.forEach((cb) => {
    try {
      cb(message);
    } catch {
      // A broken subscriber must not stop the others.
    }
  });
};

/** Path only (no query string) of an axios request config, if there is one. */
const requestPath = (config: any): string | undefined => {
  const url: unknown = config?.url;
  if (typeof url !== "string" || !url) return undefined;
  try {
    return new URL(url, config?.baseURL || window.location.origin).pathname;
  } catch {
    return url.split("?")[0];
  }
};

const isCancellation = (reason: any): boolean =>
  Boolean(
    reason &&
      (reason.__CANCEL__ ||
        reason.name === "CanceledError" ||
        reason.name === "AbortError" ||
        reason.code === "ERR_CANCELED"),
  );

/** Message + stack for whatever was passed to reject(). */
export const describeRejection = (reason: any): { message: string; stack?: string } => {
  let message: string;
  if (typeof reason === "string") {
    message = reason;
  } else if (reason && typeof reason.message === "string") {
    message = reason.message;
  } else {
    try {
      message = JSON.stringify(reason) ?? String(reason);
    } catch {
      message = String(reason);
    }
  }

  // Axios errors: name the status and endpoint so identical failures group.
  const status = reason?.response?.status;
  const path = reason?.config ? requestPath(reason.config) : undefined;
  if (status || path) {
    const method =
      typeof reason?.config?.method === "string"
        ? reason.config.method.toUpperCase()
        : undefined;
    message += ` [${[status, method, path].filter(Boolean).join(" ")}]`;
  }

  const stack = typeof reason?.stack === "string" ? reason.stack : undefined;
  return { message, stack };
};

const onWindowError = (event: ErrorEvent) => {
  try {
    const message: string = event.error?.message || event.message || "";
    if (isNoiseMessage(message) || isNoiseMessage(event.message)) return;
    notifySubscribers(message);
    reportClientError({
      message,
      stack: event.error?.stack,
      source: "window.error",
      route: currentRoute(),
    });
  } catch {
    // ignore
  }
};

const onUnhandledRejection = (event: PromiseRejectionEvent) => {
  try {
    const reason = (event as any).reason;
    if (isCancellation(reason)) return;
    const { message, stack } = describeRejection(reason);
    if (isNoiseMessage(message)) return;
    notifySubscribers(message);
    reportClientError({
      message,
      stack,
      source: "unhandledrejection",
      route: currentRoute(),
    });
  } catch {
    // ignore
  }
};

/** Register the window-level handlers once per page load (idempotent). */
export const installGlobalErrorHandlers = (): void => {
  if (handlersInstalled || typeof window === "undefined") return;
  handlersInstalled = true;
  window.addEventListener("error", onWindowError);
  window.addEventListener("unhandledrejection", onUnhandledRejection);
};
